import React from "react";
import {
  AnnotationText,
  Headline,
  Paragraph,
  HighlightedQuote,
  Section,
  ContentBlock,
} from "./ui";
import StickyHeadline from "./StickyHeadline";

// Helper function to parse compound spacing values
const parseSpacing = (spacing) => {
  // Check if it's a compound spacing like "comfortable_top_tight_bottom"
  if (spacing.includes("_top_") && spacing.includes("_bottom")) {
    const match = spacing.match(/^(.+)_top_(.+)_bottom$/);
    if (match) {
      return {
        isCompound: true,
        top: match[1],
        bottom: match[2],
      };
    }
  }
  return { isCompound: false, spacing };
};

// Individual spacing classes
const spacingTopClasses = {
  compact: "pt-8 sm:pt-12",
  comfortable: "pt-16 sm:pt-24",
  spacious: "pt-24 sm:pt-40",
  tight: "pt-4 sm:pt-6",
  none: "",
};

const spacingBottomClasses = {
  compact: "pb-8 sm:pb-12",
  comfortable: "pb-16 sm:pb-24",
  spacious: "pb-24 sm:pb-40",
  tight: "pb-4 sm:pb-6",
  none: "",
};

export const TextSectionHero = ({
  annotation,
  headline,
  lead,
  highlight,
  variant = "secondary",
  spacing = "spacious",
  maxWidth = "4xl",
  align = "left",
  className = "",
  margin = true,
  size = "lg",
  stickyHeadline = null,
  navbarHeight = 0,
  ...props
}) => {
  const parsedSpacing = parseSpacing(spacing);

  // Build custom spacing class if compound spacing is used
  const customSpacingClass = parsedSpacing.isCompound
    ? `${spacingTopClasses[parsedSpacing.top] || ""} ${
        spacingBottomClasses[parsedSpacing.bottom] || ""
      }`
    : "";

  return (
    <div className="relative mt-4">
      {/* Sticky Headline - Full Width */}
      {stickyHeadline && (
        <StickyHeadline headline={stickyHeadline} navbarHeight={navbarHeight} />
      )}

      <Section
        variant={variant}
        spacing={parsedSpacing.isCompound ? "none" : spacing}
        className={`${customSpacingClass} ${className}`}
        margin={margin}
        {...props}
      >
        <ContentBlock maxWidth={maxWidth} align={align} className="mx-auto">
          {annotation && (
            <AnnotationText variant="default" color="primary">
              {annotation}
            </AnnotationText>
          )}

          {headline && (
            <Headline
              variant={size === "md" ? "h2" : "h1"}
              color={variant === "primary" ? "secondary" : "primary"}
              className="leading-tight mb-4 sm:mb-8"
            >
              {headline}
            </Headline>
          )}

          {lead && (
            <Paragraph
              variant={size === "md" ? "large" : "lead"}
              color={variant === "primary" ? "secondary" : "primary"}
              dangerouslySetInnerHTML={
                typeof lead === "string" ? { __html: lead } : undefined
              }
              className="pb-10"
            >
              {typeof lead === "string" ? undefined : lead}
            </Paragraph>
          )}

          {highlight && (
            <HighlightedQuote
              variant={size === "md" ? "medium" : "lead"}
              color={variant === "primary" ? "secondary" : "primary"}
              noSpacing
              dangerouslySetInnerHTML={
                typeof highlight === "string"
                  ? { __html: highlight }
                  : undefined
              }
            >
              {typeof highlight === "string" ? undefined : highlight}
            </HighlightedQuote>
          )}
        </ContentBlock>
      </Section>
    </div>
  );
};

export default TextSectionHero;
